export interface DocRecord {
  id: string
  name: string
  buffer: ArrayBuffer
  byteLength: number
  currentPage: number
  lastOpened: number
}

export interface OCRCacheEntry {
  id: string
  documentName: string
  pageIndex: number
  rectKey: string
  results: string
  timestamp: number
}

export interface PreferenceEntry {
  key: string
  value: unknown
}

const DB_NAME = 'OpakorPDF'
const DB_VERSION = 1

const STORE_DOCS = 'documents'
const STORE_OCR = 'ocrCache'
const STORE_PREFS = 'preferences'

let dbPromise: Promise<IDBDatabase> | null = null

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_DOCS)) {
        db.createObjectStore(STORE_DOCS, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(STORE_OCR)) {
        db.createObjectStore(STORE_OCR, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(STORE_PREFS)) {
        db.createObjectStore(STORE_PREFS, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      dbPromise = null
      reject(request.error)
    }
  })
  return dbPromise
}

function tx(storeName: string, mode: IDBTransactionMode = 'readonly') {
  return openDB().then((db) => {
    const transaction = db.transaction(storeName, mode)
    return transaction.objectStore(storeName)
  })
}

// ── Documents ──────────────────────────────────────────

export async function saveDocument(doc: DocRecord): Promise<void> {
  const store = await tx(STORE_DOCS, 'readwrite')
  store.put(doc)
  return new Promise((resolve, reject) => {
    store.transaction.oncomplete = () => resolve()
    store.transaction.onerror = () => reject(store.transaction.error)
  })
}

export async function getDocument(id: string): Promise<DocRecord | undefined> {
  const store = await tx(STORE_DOCS)
  return new Promise((resolve, reject) => {
    const req = store.get(id)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export async function getAllDocuments(): Promise<DocRecord[]> {
  const store = await tx(STORE_DOCS)
  return new Promise((resolve, reject) => {
    const req = store.getAll()
    req.onsuccess = () => resolve(req.result.sort((a, b) => b.lastOpened - a.lastOpened))
    req.onerror = () => reject(req.error)
  })
}

export async function deleteDocument(id: string): Promise<void> {
  const store = await tx(STORE_DOCS, 'readwrite')
  store.delete(id)
  return new Promise((resolve, reject) => {
    store.transaction.oncomplete = () => resolve()
    store.transaction.onerror = () => reject(store.transaction.error)
  })
}

export async function clearAllDocuments(): Promise<void> {
  const store = await tx(STORE_DOCS, 'readwrite')
  store.clear()
  return new Promise((resolve, reject) => {
    store.transaction.oncomplete = () => resolve()
    store.transaction.onerror = () => reject(store.transaction.error)
  })
}

// ── Preferences ────────────────────────────────────────

export async function setPreference(key: string, value: unknown): Promise<void> {
  const store = await tx(STORE_PREFS, 'readwrite')
  store.put({ key, value })
  return new Promise((resolve, reject) => {
    store.transaction.oncomplete = () => resolve()
    store.transaction.onerror = () => reject(store.transaction.error)
  })
}

export async function getPreference<T = unknown>(key: string): Promise<T | undefined> {
  const store = await tx(STORE_PREFS)
  return new Promise((resolve, reject) => {
    const req = store.get(key)
    req.onsuccess = () => resolve(req.result?.value as T)
    req.onerror = () => reject(req.error)
  })
}

export async function getAllPreferences(): Promise<Record<string, unknown>> {
  const store = await tx(STORE_PREFS)
  return new Promise((resolve, reject) => {
    const req = store.getAll()
    req.onsuccess = () => {
      const prefs: Record<string, unknown> = {}
      for (const entry of req.result) {
        prefs[entry.key] = entry.value
      }
      resolve(prefs)
    }
    req.onerror = () => reject(req.error)
  })
}

export async function clearPreferences(): Promise<void> {
  const store = await tx(STORE_PREFS, 'readwrite')
  store.clear()
  return new Promise((resolve, reject) => {
    store.transaction.oncomplete = () => resolve()
    store.transaction.onerror = () => reject(store.transaction.error)
  })
}

// ── OCR Cache ──────────────────────────────────────────

export async function cacheOcrResult(entry: OCRCacheEntry): Promise<void> {
  const store = await tx(STORE_OCR, 'readwrite')
  store.put(entry)
  return new Promise((resolve, reject) => {
    store.transaction.oncomplete = () => resolve()
    store.transaction.onerror = () => reject(store.transaction.error)
  })
}

export async function getOcrCache(id: string): Promise<OCRCacheEntry | undefined> {
  const store = await tx(STORE_OCR)
  return new Promise((resolve, reject) => {
    const req = store.get(id)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export async function clearOcrCache(): Promise<void> {
  const store = await tx(STORE_OCR, 'readwrite')
  store.clear()
  return new Promise((resolve, reject) => {
    store.transaction.oncomplete = () => resolve()
    store.transaction.onerror = () => reject(store.transaction.error)
  })
}
